/** Public deployment identity shared by renderers and transports. Never store credentials here. */
export type BrandingEnvironment = Readonly<Record<string, string | undefined>>;

const text = (value: string | undefined) => value?.trim() || "";
const email = (value: string | undefined) => {
  const result = text(value);
  return /^[^\s@<>?&#]+@[^\s@<>?&#]+\.[^\s@<>?&#]+$/.test(result) ? result : "";
};
const publicUrl = (value: string | undefined) => {
  try {
    const url = new URL(text(value));
    return ["https:", "http:"].includes(url.protocol) &&
      !url.username &&
      !url.password
      ? url.href
      : "";
  } catch {
    return "";
  }
};
const color = (value: string | undefined, fallback: string) =>
  /^#[\da-f]{6}$/i.test(text(value)) ? text(value) : fallback;

export function createDeploymentBranding(env: BrandingEnvironment = {}) {
  const name = text(env.BRAND_NAME)
    .replace(/[\r\n]+/g, " ")
    .slice(0, 160);
  const supportEmail = email(env.BRAND_SUPPORT_EMAIL);
  const logoUrl = publicUrl(env.BRAND_LOGO_URL);
  const website = publicUrl(env.BRAND_WEBSITE_URL);
  const primary = color(env.BRAND_PRIMARY_COLOR, "#262626");
  const accent = color(env.BRAND_ACCENT_COLOR, "#404040");
  return {
    name,
    displayName: name || "Workspace",
    assistantName: text(env.BRAND_ASSISTANT_NAME) || "Care coordinator",
    smsSenderName: name || "Care team",
    appUrl:
      publicUrl(env.FRONTEND_URL || env.APP_URL) ||
      (env.NODE_ENV === "production" ? "" : "http://localhost:5173/"),
    intakeUrl: publicUrl(
      env.TRUSTED_INTAKE_BASE_URL || env.PATIENT_ONBOARDING_URL,
    ),
    fromEmail: email(env.MAILEROO_FROM_EMAIL) || email(env.SMTP_USER),
    fromName: text(env.MAILEROO_FROM_NAME) || name || "Care team",
    diagnosticRecipient: email(env.DIAGNOSTIC_REPORT_EMAIL),
    company: { name, supportEmail, website },
    logo: { url: logoUrl, alt: name, width: 160, height: 45 },
    colors: {
      primary,
      primaryLight: primary,
      primaryDark: primary,
      accent,
      accentLight: accent,
      accentDark: accent,
      background: "#f5f5f5",
      card: "#ffffff",
      text: "#1f2937",
      textSecondary: "#4b5563",
      textMuted: "#6b7280",
      textLight: "#737373",
      border: "#e5e7eb",
      borderLight: "#f3f4f6",
    },
    /** Persist with cached documents so a changed identity invalidates an old artifact. */
    fingerprint: JSON.stringify([
      name,
      supportEmail,
      website,
      logoUrl,
      primary,
      accent,
    ]),
  };
}

export const DASHBOARD_COLOR_KEYS = [
  "primary",
  "accent",
  "darkPrimary",
  "darkAccent",
] as const;
export type DashboardColorKey = (typeof DASHBOARD_COLOR_KEYS)[number];
export type DashboardColors = Partial<Record<DashboardColorKey, string>>;
export interface DashboardThemeSettings {
  colors: DashboardColors;
  updatedAt: string | null;
}

/** Choose the text colour with the higher WCAG contrast ratio against a six-digit hex colour. */
export function dashboardForeground(hex: string): string {
  const channels = [1, 3, 5].map(
    (start) => parseInt(hex.slice(start, start + 2), 16) / 255,
  );
  const linear = channels.map((value) =>
    value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4,
  );
  const luminance =
    0.2126 * linear[0]! + 0.7152 * linear[1]! + 0.0722 * linear[2]!;
  return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05)
    ? "#000000"
    : "#ffffff";
}

export function dashboardThemeColors(
  colors: DashboardColors,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of DASHBOARD_COLOR_KEYS) {
    const value = colors[key];
    if (value && /^#[\da-f]{6}$/i.test(value)) {
      result[key] = value;
      result[`${key}Foreground`] = dashboardForeground(value);
    }
  }
  return result;
}
